import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Mail, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, HttpError } from "@/lib/api";
import type { LogDetail, MailConfig, MailSendResponse } from "@/lib/types";
import { fmtKst, questionText } from "@/lib/utils";

/** 발신자 주소 마지막 사용값 — 브라우저별로 기억한다. */
const FROM_STORAGE_KEY = "arkiveai.mail.from";

const DEFAULT_SUBJECT = "[ArkiveAI] 질문 관련 안내드립니다";

/** 사용자 질의를 채워 넣은 기본 본문. */
function defaultBody(d: LogDetail): string {
  return [
    "질문하신 내용에 대해서 첨언 메일 드립니다.",
    "",
    "",
    "",
    `질의 내용 (${fmtKst(d.timestamp)})`,
    questionText(d.question, d.files),
    "~~~~",
    "",
    "감사합니다.",
    "",
  ].join("\n");
}

function loadSavedFrom(): string {
  try {
    return window.localStorage.getItem(FROM_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function saveFrom(v: string) {
  try {
    window.localStorage.setItem(FROM_STORAGE_KEY, v);
  } catch {
    /* 저장 실패는 무시 — 다음에 기본값으로 열릴 뿐 */
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  /** null 이면 닫힘. 값이 있으면 해당 질의 기준으로 열린다. */
  detail: LogDetail | null;
  onClose: () => void;
}

export function MailDialog({ detail, onClose }: Props) {
  const open = detail !== null;

  const { data: config } = useQuery({
    queryKey: ["mail-config"],
    queryFn: () => api.get<MailConfig>("/mail/config"),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  // 열릴 때(대상 질의가 바뀔 때) 폼 초기화
  useEffect(() => {
    if (!detail) return;
    setFrom(loadSavedFrom());
    setTo(detail.user_id ?? "");
    setSubject(DEFAULT_SUBJECT);
    setBody(defaultBody(detail));
  }, [detail]);

  // 서버 기본 발신자 — 저장된 값이 없을 때만 채운다
  useEffect(() => {
    if (open && config && !loadSavedFrom()) setFrom(config.from_email);
  }, [open, config]);

  // ESC 닫기 (발송 중에는 유지)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open || !detail) return null;

  const fromOk = EMAIL_RE.test(from.trim());
  const toOk = EMAIL_RE.test(to.trim());
  const canSend =
    !busy && (config?.configured ?? true) && fromOk && toOk &&
    subject.trim().length > 0 && body.trim().length > 0;

  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    try {
      const res = await api.post<MailSendResponse>("/mail/send", {
        to_email: to.trim(),
        from_email: from.trim(),
        subject: subject.trim(),
        body,
        query_id: detail.query_id,
      });
      saveFrom(from.trim());
      window.alert(`메일을 보냈습니다.\n받는 사람: ${res.to_email}`);
      onClose();
    } catch (e) {
      window.alert(
        e instanceof HttpError
          ? `메일 발송에 실패했습니다: ${e.message}`
          : "메일 발송에 실패했습니다.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={() => !busy && onClose()}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-xl border bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="메일 보내기"
      >
        <header className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="flex items-center gap-2 font-semibold">
            <Mail className="h-4 w-4" />
            메일 보내기
          </h3>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={busy} aria-label="닫기">
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="space-y-3 overflow-y-auto p-5">
          {config && !config.configured && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
              SMTP 가 설정되지 않아 발송할 수 없습니다. 서버 .env.portal 에
              SMTP_USER / SMTP_PASSWORD 를 추가한 뒤 다시 시도하세요.
            </p>
          )}

          <Field label="보내는 사람">
            <Input
              type="email"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              placeholder="leoyun2010@gmail.com"
            />
          </Field>

          <Field label="받는 사람">
            <Input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="사용자 이메일"
            />
            {detail.user_name && (
              <p className="mt-1 text-xs text-muted-foreground">
                질문 사용자: {detail.user_name} ({detail.user_id ?? "이메일 미기록"})
              </p>
            )}
          </Field>

          <Field label="제목">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>

          <Field label="본문">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </Field>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            취소
          </Button>
          <Button size="sm" className="gap-1" onClick={() => void send()} disabled={!canSend}>
            <Mail className="h-4 w-4" />
            {busy ? "보내는 중…" : "보내기"}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-semibold text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
